// langPath.js
'use strict';

import { DEFAULT_LANGUAGE, PREFERRED_LOCALES } from '../resource/i18nCommon.js';

/**
 * Language pathname prefixes (excludes default language zh-TW)
 * e.g., /en/xxx, /ja/xxx, /zh/xxx
 * Default language uses root path without prefix: /xxx
 */
export const PATHNAME_PREFIXES_WITHOUT_DEFAULT_LANG = PREFERRED_LOCALES.filter(
  locale => locale !== DEFAULT_LANGUAGE
);

// Lowercase version for case-insensitive comparison
const LOWER_PREFIXES = PATHNAME_PREFIXES_WITHOUT_DEFAULT_LANG.map(p =>
  p.toLowerCase()
);

/**
 * Get language prefix from URL pathname
 * @param {string} pathname - URL pathname
 * @returns {string|null} Language code or null
 */
export const getLangFromPath = pathname => {
  if (!pathname) return null;

  const segments = pathname.split('/').filter(Boolean);
  const firstSegment = segments[0]?.toLowerCase();

  if (LOWER_PREFIXES.includes(firstSegment)) {
    return firstSegment;
  }

  return null;
};

/**
 * Remove language prefix from pathname
 * @param {string} pathname - URL pathname
 * @returns {string} Pathname without language prefix
 */
export const removeLangFromPath = pathname => {
  if (!pathname) return '/';

  const lang = getLangFromPath(pathname);
  if (!lang) return pathname;

  const segments = pathname.split('/').filter(Boolean);
  segments.shift();

  return '/' + segments.join('/');
};

/**
 * Generate path with language prefix
 * @param {string} pathname - Original path (without language prefix)
 * @param {string} lang - Target language
 * @returns {string} Path with language prefix
 */
export const generateLangPath = (pathname, lang) => {
  const cleanPath = removeLangFromPath(pathname);

  // Default language uses root path without prefix
  if (!lang || lang === DEFAULT_LANGUAGE) {
    return cleanPath || '/';
  }

  // Normalize language and check if it's a supported prefix
  const normalized = normalizeLang(lang);
  if (LOWER_PREFIXES.includes(normalized.toLowerCase())) {
    return `/${normalized}${cleanPath === '/' ? '' : cleanPath}`;
  }

  return cleanPath || '/';
};

/**
 * Normalize language code to standard format
 * @param {string} lang - Language code (may come from URL)
 * @returns {string} Normalized language code
 */
export const normalizeLang = lang => {
  if (!lang) return DEFAULT_LANGUAGE;

  const lower = lang.toLowerCase();

  if (lower === 'zh' || lower === 'zh-cn' || lower === 'zh-hans') {
    return 'cn';
  }
  if (lower === 'zh-tw' || lower === 'zh-hant') {
    return 'zh-TW';
  }

  return lower;
};

export const getHreflang = lang => {
  const normalizedLang = normalizeLang(lang);
  switch (normalizedLang) {
    case 'zh-TW':
      return 'zh-Hant';
    case 'cn':
      return 'zh-Hans';
    default:
      return normalizedLang;
  }
};

/**
 * Generate paths with language prefix variants
 * @param {string[]} paths - Original paths
 * @returns {string[]} Paths including language prefix variants
 */
export const generateLangAwarePaths = paths => {
  const result = [...paths];
  paths.forEach(path => {
    PATHNAME_PREFIXES_WITHOUT_DEFAULT_LANG.forEach(lang => {
      result.push(`/${lang}${path === '/' ? '' : path}`);
    });
  });
  return result;
};

/**
 * Create regex with optional language prefix support
 * @param {RegExp} regex - Original regex
 * @returns {RegExp} Regex supporting language prefix
 */
export const createLangAwareRegExp = regex => {
  const prefixPattern = `(?:/(${PATHNAME_PREFIXES_WITHOUT_DEFAULT_LANG.join('|')}))?`;
  const source = regex.source;
  const flags = regex.flags;

  const newSource = source.replace(/^\^\\\//, `^${prefixPattern}\\/`);

  return new RegExp(newSource, flags);
};
